import { useMemo, useSyncExternalStore } from 'react';
import type { BillingRegistryGetUsageResponse } from '@tryiris-ai/common.protojs/api/v1/billing_registry';

type CameraReadStatus = 'unknown' | 'loaded' | 'error';
export interface TrialCameraRead {
  readonly roomId: string;
  readonly generation: number;
}
let roomId: string | undefined;
let generation = 0;
let orderedKeys: readonly string[] = [];
const listeners = new Set<() => void>();
const emptyOrder: readonly string[] = [];
let refreshCurrentRoom: ((requestedRoomId: string) => Promise<void>) | undefined;

// The billing monitor owns transport; configuration writers notify it through
// this late-bound callback to avoid a broker -> limits -> broker import cycle.
export function setTrialCameraRefresher(refresh: (requestedRoomId: string) => Promise<void>): void {
  refreshCurrentRoom = refresh;
}

export async function refreshTrialCameraConfiguration(requestedRoomId: string): Promise<void> {
  if (requestedRoomId === roomId && refreshCurrentRoom !== undefined)
    await refreshCurrentRoom(requestedRoomId);
}

function emit(): void {
  for (const listener of listeners) listener();
}
function cameraKey(agentId: string, deviceId: string): string {
  return `${agentId}/${deviceId}`;
}

// This store deliberately has no broker dependency: billing loads before the
// agent graph, and live-agent disappearance must never promote another camera.
// Per absent-data-gates-permissively (ai.knowledge/global).
export function resetTrialCameraAccess(nextRoomId: string): void {
  roomId = nextRoomId;
  generation += 1;
  orderedKeys = [];
  emit();
}

export function beginTrialCameraRead(nextRoomId: string): TrialCameraRead {
  resetTrialCameraAccess(nextRoomId);
  return { roomId: nextRoomId, generation };
}

export function isCurrentTrialCameraRead(read: TrialCameraRead): boolean {
  return read.roomId === roomId && read.generation === generation;
}

// A stale success cannot overwrite a new failure, changed Studio or lifetime.
// Reject the complete order on malformed/missing metadata rather than selecting
// a plausible-looking subset and accidentally locking the real oldest camera.
export function finishTrialCameraRead(
  read: TrialCameraRead,
  response: BillingRegistryGetUsageResponse | null,
  status: CameraReadStatus
): boolean {
  if (!isCurrentTrialCameraRead(read)) return false;
  orderedKeys = [];
  if (
    status === 'loaded' &&
    response !== null &&
    response.error === undefined &&
    response.limits !== undefined &&
    response.trialCameraRestrictions === true &&
    response.trialCameraOrder.length > 0
  ) {
    const keys: string[] = [];
    for (const camera of response.trialCameraOrder) {
      if (camera.agentId.length !== 16 || camera.deviceId === '') {
        emit();
        return true;
      }
      const agentId = Array.from(camera.agentId, (byte) => byte.toString(16).padStart(2, '0')).join(
        ''
      );
      const key = cameraKey(agentId, camera.deviceId);
      if (keys.includes(key)) {
        emit();
        return true;
      }
      keys.push(key);
    }
    orderedKeys = keys;
  }
  emit();
  return true;
}

export function isTrialCameraLocked(
  cameraRoomId: string | undefined,
  agentId: string | undefined,
  deviceId: string | undefined
): boolean {
  if (cameraRoomId !== roomId || agentId === undefined || deviceId === undefined) return false;
  const index = orderedKeys.indexOf(cameraKey(agentId, deviceId));
  return index > 0;
}

// LiveKit may announce a device before its proxy exists. Only enforce when
// every matching persisted identity is locked; an ambiguous identity stays
// permissive rather than suppressing the oldest camera's subscription.
export function isTrialCameraLockedDevice(cameraRoomId: string, deviceId: string): boolean {
  if (cameraRoomId !== roomId) return false;
  const matches = orderedKeys
    .map((key, index) => ({ key, index }))
    .filter(({ key }) => key.endsWith(`/${deviceId}`));
  return matches.length > 0 && matches.every(({ index }) => index > 0);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Presentation uses the same validated persisted order as enforcement. In
// particular, the first configured camera need not be first in the sidebar or online.
export function useTrialCameraOrder(cameraRoomId: string | undefined): readonly {
  readonly agentId: string;
  readonly deviceId: string;
}[] {
  const keys = useSyncExternalStore(
    subscribe,
    () => (cameraRoomId === roomId ? orderedKeys : emptyOrder),
    () => emptyOrder
  );
  return useMemo(
    () =>
      keys.map((key) => {
        const separator = key.indexOf('/');
        return { agentId: key.slice(0, separator), deviceId: key.slice(separator + 1) };
      }),
    [keys]
  );
}

export function useTrialCameraLocked(
  cameraRoomId: string | undefined,
  agentId: string | undefined,
  deviceId: string | undefined
): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isTrialCameraLocked(cameraRoomId, agentId, deviceId),
    () => false
  );
}
